import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import defaultContent from "../../../content/downlineProfile";
import TabSwitcher from "../../../common/components/TabSwitcher/TabSwitcher";
import "../PlatinumProfile/DownlineProfile.scss";
import CustomerProfileBanner from "../Header/ProfileBanner/CustomerProfileBanner";
import KeyInformation from "../Tabs/KeyInformation/KeyInformation";
import Notes from "../Tabs/Notes/Notes";
import ExportButton from "../Export/ExportButton/ExportButton";
import { useAppDispatch, useAppSelector } from "../../../store";
import { trackTabKeyInformation, trackTabNotes } from "../PlatinumProfile/downlineProfileTealium";
import { SopStatus } from "../Tabs/SopStatus/SopStatus";
import { getSopStatusForAbo } from "../Tabs/SopStatus/sopStatusData";
import { CustomerProfileTabs, CUSTOMER_PROFILE_TABS, setActiveTab } from "../../../reducers/customerProfile";
import { IconNoResults } from "../../../common/icons/IconNoResults/IconNoResults";

type Props = {
  affAbo: string;
  pushToHistory?: (affAbo: string) => void;
};

const CustomerProfile = ({ affAbo: _affAbo, pushToHistory }: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("downlineProfile");
  const { profile } = useAppSelector((store) => store.boot.configuration);
  const activeTab = useAppSelector((s) => s.downlineProfile.activeTab);
  const initialTab = useAppSelector((s) => s.profile.downlineProfileInitialTab);
  const sopStatus = getSopStatusForAbo(_affAbo);
  const showDownlineProfileExport =
    useAppSelector((s) => s.boot.configuration.profile?.showDownlineProfileExport as boolean) ?? true;
  const tabsScrollRef = useRef<HTMLDivElement>(null);

  const TAB_VIEWS: Record<CustomerProfileTabs, JSX.Element> = {
    [CUSTOMER_PROFILE_TABS.ORDER_HISTORY]: <div>ORDER HISTORY PLACEHOLDER</div>,
    [CUSTOMER_PROFILE_TABS.SOP_STATUS]: profile?.comingSoon?.sopStatus ? (
      <div className="downline-profile__comingSoonTile">
        <IconNoResults />
        <p>{t("comingSoon.sopStatus", defaultContent.comingSoon.sopStatus)}</p>
      </div>
    ) : (
      <SopStatus />
    ),
    [CUSTOMER_PROFILE_TABS.COUPONS]: (
      <div className="downline-profile__comingSoonTile">
        <IconNoResults />
        <p>{t("comingSoon.coupons", defaultContent.comingSoon.coupons)}</p>
      </div>
    ),
    [CUSTOMER_PROFILE_TABS.KEY_INFO]: <KeyInformation />,
    [CUSTOMER_PROFILE_TABS.NOTES]: <Notes key={_affAbo} />,
  };

  const tabs = [
    {
      id: CUSTOMER_PROFILE_TABS.ORDER_HISTORY,
      text: t("tabs.orderHistory", defaultContent.tabs.orderHistory as string),
    },
    {
      id: CUSTOMER_PROFILE_TABS.SOP_STATUS,
      text: t("tabs.sopStatus", defaultContent.tabs.sopStatus as string),
      notification: profile?.comingSoon?.sopStatus ? 0 : sopStatus.kpis.productsOnSop,
    },
    {
      id: CUSTOMER_PROFILE_TABS.COUPONS,
      text: t("tabs.coupons", defaultContent.tabs.coupons as string),
    },
    {
      id: CUSTOMER_PROFILE_TABS.KEY_INFO,
      text: t("tabs.keyInformation", defaultContent.tabs.keyInformation as string),
    },
    { id: CUSTOMER_PROFILE_TABS.NOTES, text: t("tabs.notes", defaultContent.tabs.notes as string) },
  ];

  useEffect(() => {
    dispatch(setActiveTab(initialTab ?? CUSTOMER_PROFILE_TABS.ORDER_HISTORY));
  }, [dispatch, initialTab]);

  useEffect(() => {
    const scrollContainer = tabsScrollRef.current;
    const tabElement = scrollContainer?.querySelector<HTMLElement>(`#TabSwitcher_${activeTab}`);
    if (!scrollContainer || !tabElement) return;

    const SCROLL_GAP = 8;
    const containerRect = scrollContainer.getBoundingClientRect();
    const tabRect = tabElement.getBoundingClientRect();
    const overflowRight = tabRect.right + SCROLL_GAP - containerRect.right;
    const overflowLeft = containerRect.left - (tabRect.left - SCROLL_GAP);

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const behavior: ScrollBehavior = reducedMotion ? "auto" : "smooth";

    if (overflowRight > 0) {
      scrollContainer.scrollBy({ left: overflowRight, behavior });
    } else if (overflowLeft > 0) {
      scrollContainer.scrollBy({ left: -overflowLeft, behavior });
    }
  }, [activeTab]);

  return (
    <div className="downline-profile" data-testid="downline-profile">
      <div className="downline-profile__banner" data-testid="downline-profile-banner" aria-live="polite">
        <div className="downline-profile__banner__nav-buttons"></div>
        <CustomerProfileBanner pushToHistory={pushToHistory} />
      </div>
      <div className="downline-profile__content" data-testid="downline-profile-content">
        <div className="downline-profile__header">
          <div className="downline-profile__tabs" ref={tabsScrollRef}>
            <TabSwitcher
              Tabs={tabs.filter((tab) => profile.customerProfileTabsV2.includes(tab.id))}
              selectedTab={activeTab}
              onTabClick={(id) => {
                dispatch(setActiveTab(id as CustomerProfileTabs));
                switch (id) {
                  case CUSTOMER_PROFILE_TABS.KEY_INFO:
                    trackTabKeyInformation();
                    break;
                  case CUSTOMER_PROFILE_TABS.NOTES:
                    trackTabNotes();
                    break;
                }
              }}
            />
          </div>
          <div className="downline-profile__toolbar">{showDownlineProfileExport && <ExportButton />}</div>
        </div>

        <div className="downline-profile__body" data-testid="downline-profile-body">
          {TAB_VIEWS[activeTab]}
        </div>
      </div>
    </div>
  );
};

export default CustomerProfile;
